// src/components/common/DashboardHeader.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  useTheme,
  Badge,
  useMediaQuery
} from '@mui/material';
import { 
  Menu as MenuIcon,
  LogOut,
  Settings,
  Bell,
  CheckSquare
} from 'lucide-react';
import agthealthService from '../../../services/agthealthService';

const DashboardHeader = ({ 
  title = 'Dashboard', 
  user = {}, 
  isMobile = false, 
  onMenuClick,
  onLogout,
  onSettings
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileDevice = isMobile || isSmallScreen;
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const open = Boolean(anchorEl);
  const notificationOpen = Boolean(notificationAnchorEl);

  // Función para extraer nombre del usuario con múltiples fallbacks
  const getUserDisplayData = () => {
    const safeUser = user || {};
    const safeProfile = profileData || {};
    
    // Combinar datos de ambas fuentes, priorizando profileData
    // IMPORTANTE: Los campos deben coincidir con DynamoDB
    const userData = {
      firstName: safeProfile.firstName || safeUser.firstName || '',
      lastName: safeProfile.lastName || safeUser.lastName || '',
      email: safeProfile.email || safeUser.email || '',
      role: safeProfile.role || safeUser.role || '',
      specialtyArea: safeProfile.specialtyArea || safeUser.specialtyArea || '', // Campo correcto de DynamoDB
      phoneNumber: safeProfile.phoneNumber || safeUser.phoneNumber || '',
      isVerified: safeProfile.isVerified || safeUser.isVerified || false
    };
    
    // Estrategia de fallback para obtener el nombre completo
    let displayName = '';
    
    // 1. Si hay firstName y lastName (campos principales de DynamoDB)
    if (userData.firstName && userData.lastName) {
      displayName = `${userData.firstName} ${userData.lastName}`;
    }
    // 2. Si hay solo firstName o lastName
    else if (userData.firstName) {
      displayName = userData.firstName;
    } else if (userData.lastName) {
      displayName = userData.lastName;
    }
    // 3. Usar parte del email como fallback
    else if (userData.email) {
      displayName = userData.email.split('@')[0];
    }
    // 4. Fallback final
    else {
      displayName = 'Usuario';
    }
    
    // Obtener inicial para el avatar
    const getInitial = () => {
      if (userData.firstName) return userData.firstName.charAt(0).toUpperCase();
      if (userData.lastName) return userData.lastName.charAt(0).toUpperCase();
      if (userData.email) return userData.email.charAt(0).toUpperCase();
      return 'U';
    };
    
    // Obtener rol/especialidad usando campos de DynamoDB
    const getRole = () => {
      if (userData.specialtyArea && userData.specialtyArea !== 'Otro') {
        return userData.specialtyArea;
      }
      if (userData.role === 'independent_professional') {
        return 'Profesional Independiente';
      }
      if (userData.role) {
        return userData.role;
      }
      return 'Profesional de la salud';
    };
    
    return {
      displayName: displayName.trim(),
      initial: getInitial(),
      role: getRole(),
      rawData: userData // Para debugging
    };
  };

  // Obtener datos del usuario
  const { displayName: userFullName, initial: userInitial, role: userRole, rawData } = getUserDisplayData();

  // DEBUGGING TEMPORAL - Remover después de confirmar que funciona
  console.log('=== DEBUG USER DATA ===');
  console.log('User prop:', user);
  console.log('Profile data:', profileData);
  console.log('Combined data:', rawData);
  console.log('Display name:', userFullName);
  console.log('Initial:', userInitial);
  console.log('Role:', userRole);
  console.log('======================');

  // Cargar tareas pendientes y perfil del usuario
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Cargar tareas pendientes
        const tasksResponse = await agthealthService.getUserTasks({ completed: false });
        if (tasksResponse.success) {
          setPendingTasks(tasksResponse.data || []);
        }
        
        // Cargar perfil completo del usuario
        console.log('Fetching user profile...');
        const profileResponse = await agthealthService.getUserProfile();
        console.log('Profile response:', profileResponse);
        
        if (profileResponse.success && profileResponse.data) {
          console.log('Setting profile data:', profileResponse.data);
          setProfileData(profileResponse.data);
        } else {
          console.error('Profile response not successful:', profileResponse);
        }
      } catch (error) {
        console.error('Error al cargar datos del usuario:', error);
      }
    };
    
    fetchData();
  }, []);
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleNotificationClick = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };
  
  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };
  
  const handleLogout = () => {
    handleClose();
    if (onLogout) onLogout();
  };
  
  const handleSettings = () => {
    handleClose();
    if (onSettings) onSettings();
  };
  
  // Determinar si hay una imagen de perfil disponible
  const hasProfileImage = profileData && (profileData.profileImageUrl || profileData.avatar || profileData.profileImage);
  
  return (
    <AppBar 
      position="static" 
      color="transparent" 
      elevation={0}
      sx={{ 
        mb: isMobileDevice ? 2 : 3, 
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper
      }}
    >
      <Toolbar 
        variant={isMobileDevice ? "dense" : "regular"}
        sx={{
          minHeight: isMobileDevice ? '56px !important' : '64px !important',
          px: isMobileDevice ? 1 : 3
        }}
      >
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={onMenuClick}
          sx={{ 
            mr: isMobileDevice ? 1 : 2,
            p: isMobileDevice ? 1 : 1.5
          }}
        >
          <MenuIcon size={isMobileDevice ? 20 : 24} />
        </IconButton>
        
        <Typography 
          variant={isMobileDevice ? "subtitle1" : "h6"} 
          component="div" 
          sx={{ 
            flexGrow: 1,
            fontSize: isMobileDevice ? '1rem' : '1.25rem',
            fontWeight: isMobileDevice ? 500 : 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: isMobileDevice ? 'calc(100vw - 120px)' : 'none'
          }}
        >
          {title}
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: isMobileDevice ? 0.5 : 1
        }}>
          {/* Icono de notificaciones con badge */}
          <IconButton
            onClick={handleNotificationClick}
            size={isMobileDevice ? "small" : "medium"}
            sx={{ 
              p: isMobileDevice ? 0.75 : 1
            }}
            aria-controls={notificationOpen ? 'notification-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={notificationOpen ? 'true' : undefined}
          >
            <Badge 
              badgeContent={pendingTasks.length} 
              color="error"
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: isMobileDevice ? '0.625rem' : '0.75rem',
                  minWidth: isMobileDevice ? '16px' : '20px',
                  height: isMobileDevice ? '16px' : '20px'
                }
              }}
            >
              <Bell size={isMobileDevice ? 18 : 20} />
            </Badge>
          </IconButton>
          
          <IconButton
            onClick={handleClick}
            size={isMobileDevice ? "small" : "medium"}
            sx={{ 
              p: isMobileDevice ? 0.5 : 1
            }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <Avatar 
              src={hasProfileImage ? (profileData.profileImageUrl || profileData.avatar || profileData.profileImage) : undefined}
              alt={userFullName}
              sx={{ 
                width: isMobileDevice ? 28 : 32, 
                height: isMobileDevice ? 28 : 32, 
                bgcolor: 'primary.main',
                fontSize: isMobileDevice ? '0.875rem' : '1rem'
              }}
            >
              {!hasProfileImage && userInitial}
            </Avatar>
          </IconButton>
        </Box>
        
        {/* Menú de notificaciones */}
        <Menu
          anchorEl={notificationAnchorEl}
          id="notification-menu"
          open={notificationOpen}
          onClose={handleNotificationClose}
          onClick={handleNotificationClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
              mt: 1.5,
              borderRadius: 2,
              minWidth: isMobileDevice ? '280px' : '320px',
              maxWidth: isMobileDevice ? '90vw' : '400px',
              maxHeight: isMobileDevice ? '70vh' : '400px',
              overflowY: 'auto',
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{
            paper: {
              sx: {
                // Asegurar que el menú no se salga de la pantalla en móvil
                ...(isMobileDevice && {
                  left: '8px !important',
                  right: '8px !important',
                  width: 'calc(100vw - 16px)',
                  maxWidth: 'calc(100vw - 16px)'
                })
              }
            }
          }}
        >
          <MenuItem 
            sx={{ 
              justifyContent: 'space-between',
              py: isMobileDevice ? 1.5 : 1,
              px: isMobileDevice ? 2 : 1.5
            }}
          >
            <Typography variant={isMobileDevice ? "body2" : "subtitle2"} fontWeight="bold">
              Tareas Pendientes
            </Typography>
            <Typography 
              variant="caption" 
              color="primary"
              sx={{ 
                fontSize: isMobileDevice ? '0.75rem' : '0.875rem',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Ver todas
            </Typography>
          </MenuItem>
          
          <Divider />
          
          {pendingTasks.length === 0 ? (
            <MenuItem sx={{ py: isMobileDevice ? 2 : 1.5, px: isMobileDevice ? 2 : 1.5 }}>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ textAlign: 'center', width: '100%' }}
              >
                No hay tareas pendientes
              </Typography>
            </MenuItem>
          ) : (
            pendingTasks.slice(0, 5).map(task => (
              <MenuItem 
                key={task.taskId} 
                sx={{ 
                  py: isMobileDevice ? 1.5 : 1,
                  px: isMobileDevice ? 2 : 1.5,
                  alignItems: 'flex-start'
                }}
              >
                <ListItemIcon sx={{ minWidth: isMobileDevice ? 32 : 36, mt: 0.5 }}>
                  <CheckSquare size={isMobileDevice ? 16 : 18} />
                </ListItemIcon>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      fontSize: isMobileDevice ? '0.875rem' : '0.875rem',
                      lineHeight: 1.3
                    }}
                  >
                    {task.title}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      display: 'block',
                      mt: 0.5,
                      fontSize: isMobileDevice ? '0.75rem' : '0.75rem'
                    }}
                  >
                    {task.dueDate ? `Vence: ${task.dueDate}` : 'Sin fecha'}
                  </Typography>
                </Box>
              </MenuItem>
            ))
          )}
        </Menu>
        
        {/* Menú de usuario */}
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
              mt: 1.5,
              borderRadius: 2,
              minWidth: isMobileDevice ? '200px' : '220px',
              maxWidth: isMobileDevice ? '90vw' : '280px',
              '& .MuiAvatar-root': {
                width: isMobileDevice ? 28 : 32,
                height: isMobileDevice ? 28 : 32,
                ml: -0.5,
                mr: 1,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{
            paper: {
              sx: {
                // Asegurar que el menú no se salga de la pantalla en móvil
                ...(isMobileDevice && {
                  left: '8px !important',
                  right: '8px !important',
                  width: 'calc(100vw - 16px)',
                  maxWidth: 'calc(100vw - 16px)'
                })
              }
            }
          }}
        >
          <MenuItem sx={{ 
            py: isMobileDevice ? 1.5 : 1,
            px: isMobileDevice ? 2 : 1.5
          }}>
            <Avatar 
              src={hasProfileImage ? (profileData.profileImageUrl || profileData.avatar || profileData.profileImage) : undefined}
              alt={userFullName}
              sx={{ 
                bgcolor: 'primary.main',
                width: isMobileDevice ? 28 : 32,
                height: isMobileDevice ? 28 : 32,
                fontSize: isMobileDevice ? '0.875rem' : '1rem'
              }}
            >
              {!hasProfileImage && userInitial}
            </Avatar>
            <Box sx={{ ml: 1, flex: 1, minWidth: 0 }}>
              <Typography 
                variant="body1"
                sx={{ 
                  fontSize: isMobileDevice ? '0.875rem' : '1rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {userFullName}
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  fontSize: isMobileDevice ? '0.75rem' : '0.75rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block'
                }}
              >
                {userRole}
              </Typography>
            </Box>
          </MenuItem>
          
          <Divider />
          
          <MenuItem 
            onClick={handleSettings} 
            sx={{ 
              py: isMobileDevice ? 1.5 : 1.5,
              px: isMobileDevice ? 2 : 1.5
            }}
          >
            <ListItemIcon sx={{ minWidth: isMobileDevice ? 32 : 36 }}>
              <Settings size={isMobileDevice ? 16 : 18} />
            </ListItemIcon>
            <Typography 
              variant="body2"
              sx={{ fontSize: isMobileDevice ? '0.875rem' : '0.875rem' }}
            >
              Configuración
            </Typography>
          </MenuItem>
          
          <MenuItem 
            onClick={handleLogout} 
            sx={{ 
              py: isMobileDevice ? 1.5 : 1.5,
              px: isMobileDevice ? 2 : 1.5
            }}
          >
            <ListItemIcon sx={{ minWidth: isMobileDevice ? 32 : 36 }}>
              <LogOut size={isMobileDevice ? 16 : 18} />
            </ListItemIcon>
            <Typography 
              variant="body2"
              sx={{ fontSize: isMobileDevice ? '0.875rem' : '0.875rem' }}
            >
              Cerrar sesión
            </Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default DashboardHeader;